import { isVisibleInViewport } from "../../../assets/js/utils/utils";

const SELECTORS = {
  menu: {
    active: "Menu--js",
    asTabs: "Menu--asTabs",
  },
  select: {
    default: "Menu-select",
    template: "Menu-selectTemplate",
  },
  list: "Menu-list",
  listItem: "Menu-listItem",
};

class Menu {
  /**
   *
   * @param {HTMLElement} menu
   */
  constructor(menu) {
    this.menu = menu;
    this.expanded = false;
    this.menuAsTabs = false;

    this._setBindings();

    // Handles the dropdown visibility changes
    this.intersectionObserver = new IntersectionObserver(
      this._handleIntersection
    );

    this._queryElements();
    this._activateMenu();
    this._addEventListener();
  }

  /**
   * Set bindings for callbacks
   */
  _setBindings() {
    this._handleIntersection = this._handleIntersection.bind(this);
  }

  /**
   * Query all necessary elements
   */
  _queryElements() {
    this.select = this.menu.querySelector(`.${SELECTORS.select.default}`);
    this.selectTemplates = this.menu.querySelectorAll(
      `.${SELECTORS.select.template}`
    );
    this.list = this.menu.querySelector(`.${SELECTORS.list}`);
    this.listItems = [...this.list.querySelectorAll(`.${SELECTORS.listItem}`)];
    this.menuAsTabs = this.menu.classList.contains(SELECTORS.menu.asTabs);
    if (this.menuAsTabs) {
      const currentListItem = this.listItems.find(
        (listItem) =>
          listItem.querySelector("button").getAttribute("aria-selected") ===
          "true"
      );
      if (currentListItem) {
        this.currentListItemButton = currentListItem.querySelector("button");
      }
    }
  }

  /**
   * Add class to enable progressive enhancements by js
   */
  _activateMenu() {
    this.menu.classList.add(SELECTORS.menu.active);
  }

  /**
   * Listen to click events on the select and listItems as tabs as well as
   * change in visibility of the dropdown
   */
  _addEventListener() {
    this.select.addEventListener("click", () => this._handleSelectClick());
    this.menu.addEventListener("keydown", (event) =>
      this._handleMenuKeyDown(event)
    );
    this.intersectionObserver.observe(this.list);
    if (this.menuAsTabs) {
      this.listItems.forEach((listItem) => {
        listItem
          .querySelector("button")
          .addEventListener("click", (event) =>
            this._handleListItemClick(event.currentTarget)
          );
      });
    }
  }

  /**
   * Handle the listItemButton click, toggle the aria-selected respectively
   * and call the updateSelect Method to make sure the selectButton updates
   * accordingly
   *
   * @param {EventTarget} listItemButton
   */
  _handleListItemClick(listItemButton) {
    if (listItemButton !== this.currentListItemButton) {
      if (this.currentListItemButton) {
        this.currentListItemButton.setAttribute("aria-selected", false);
      }
      listItemButton.setAttribute("aria-selected", true);
      this.currentListItemButton = listItemButton;
    }
    this._updateSelect();
  }

  /**
   * If the user escapes somewhere in the menu then
   * the the menu should close itself
   *
   * @param {Event} event
   */
  _handleMenuKeyDown(event) {
    switch (event.key) {
      case "Escape":
        this._closeMenu();
        break;
      case "ArrowDown":
        event.preventDefault();
        this._moveFocusDown(event.target);
        break;
      case "ArrowUp":
        event.preventDefault();
        this._moveFocusUp(event.target);
        break;
      default:
        break;
    }
  }

  /**
   * Handle the button click to open or close the menu
   */
  _handleSelectClick() {
    if (this.expanded) {
      this.expanded = false;
    } else {
      this.expanded = true;
    }
    this._setExpandedAttribute();
  }

  /**
   * This simply closes the menu
   */
  _closeMenu() {
    this.expanded = false;
    this._setFocusOnSelect();
    this._setExpandedAttribute();
  }

  /**
   * Move the focus to a higher indexed listItem if possible,
   * if last is already selected do nothing
   *
   * @param {HTMLElement} currentListItem
   */
  _moveFocusDown(currentListItem) {
    const currentListItemIndex = this._getCurrentListItemIndex(currentListItem);
    if (currentListItemIndex < this.listItems.length - 1) {
      this._moveListItemFocus(currentListItemIndex, 1);
    }
  }

  /**
   * Move the focus to a lower indexed listItem if possible,
   * else focus the select
   *
   * @param {HTMLElement} currentListItem
   */
  _moveFocusUp(currentListItem) {
    const currentListItemIndex = this._getCurrentListItemIndex(currentListItem);
    if (currentListItemIndex >= 1) {
      this._moveListItemFocus(currentListItemIndex, -1);
    } else {
      this.select.focus();
    }
  }

  /**
   * If the observer detects a change in visibility this method calls the
   * corresponding function to set the aria-expanded attribute
   */
  _handleIntersection() {
    if (this.list.offsetParent === null || !isVisibleInViewport(this.list)) {
      this.expanded = false;
    }
    this._setExpandedAttribute();
  }

  /**
   * This replicates the matching template and replace the existing
   * select button with it
   */
  _updateSelect() {
    const matchingTemplate = [...this.selectTemplates].find((template) =>
      template.content.querySelector(
        `button[data-related-list-item="${this.currentListItemButton.id}"]`
      )
    );
    this.select.replaceWith(matchingTemplate.content.cloneNode(true));
    // Update all class variables and reassign eventListener to new elements
    this._queryElements();
    this._updateEventListenerOnChange();
  }

  /**
   * In case the select field has changed the eventListener needs an update
   */
  _updateEventListenerOnChange() {
    this.select.addEventListener("click", () => this._handleSelectClick());
  }

  /**
   * Sets the aria-expanded attribute on the button element
   */
  _setExpandedAttribute() {
    this.select.setAttribute("aria-expanded", this.expanded);
  }

  /**
   * Set the focus on the select element and prevent scrolling
   */
  _setFocusOnSelect() {
    this.select.focus({ preventScroll: true });
  }

  /**
   * This finds the current listItems index if possible,
   * otherwise returns -1
   *
   * @param {HTMLElement} child
   * @returns {number}
   */
  _getCurrentListItemIndex(child) {
    return this.listItems.findIndex((listItem) => listItem.contains(child));
  }

  /**
   * This moves the focus for a given offset to another
   * listItems focusable element (anchor/button)
   *
   * @param {number} listItemIndex
   * @param {number} offset
   */
  _moveListItemFocus(listItemIndex, offset) {
    this.listItems[listItemIndex + offset].querySelector("a, button").focus();
  }
}

export default Menu;
